export type WorkTool =
  | "rectangle-area"
  | "circle-area"
  | "lasso-area"
  | "line"
  | "arrow"
  | "pen"
  | "bezier"
  | "text"
  | "text-field";
const tools: Record<string, WorkTool> = {
  "tool-marquee": "rectangle-area",
  "tool-circle": "circle-area",
  "tool-lasso": "lasso-area",
  "add-line": "line",
  "add-arrow": "arrow",
  "draw-pen": "pen",
  "draw-bezier": "bezier",
  "add-text-title": "text",
  "add-text-field": "text-field",
};
/** Original grouped tool menus, independent of document and rendering. */
export class NativeToolGroups<T extends string = WorkTool> {
  private current = new Map<HTMLElement, HTMLButtonElement>();
  constructor(
    private activate: (tool: T) => void,
    groups: Iterable<HTMLElement> = document.querySelectorAll<HTMLElement>(
      '[data-tool-group-container]:not([data-tool-group-container="image"])',
    ),
    private mapping: Record<string, T> = tools as Record<string, T>,
  ) {
    for (const group of groups) {
      const trigger = group.querySelector<HTMLButtonElement>(
          "[data-tool-group-trigger]",
        )!,
        menu = group.querySelector<HTMLElement>(".dropdown-content")!,
        choices = [...menu.querySelectorAll<HTMLButtonElement>("button")];
      this.current.set(group, choices[0]!);
      menu.hidden = true;
      const open = () => {
        const opening = menu.hidden;
        this.close();
        menu.hidden = !opening;
        trigger.setAttribute("aria-expanded", String(opening));
      };
      const choose = (button: HTMLButtonElement) => {
        this.current.set(group, button);
        trigger
          .querySelector("use")!
          .setAttribute("href", "#" + button.dataset.toolIcon);
        trigger.title = trigger.ariaLabel = button.dataset.toolLabel!;
        this.close();
        activate(this.mapping[button.id]!);
      };
      let ignoreClick = false,
        touch = false;
      trigger.onclick = (e) => {
        if (ignoreClick) {
          ignoreClick = false;
          return;
        }
        if (
          matchMedia("(max-width:1100px)").matches ||
          (e as PointerEvent).pointerType === "touch"
        )
          open();
        else choose(this.current.get(group)!);
      };
      trigger.ondblclick = (e) => {
        e.preventDefault();
        // Touch clicks already toggle the menu; ignore the compatibility dblclick.
        if (!touch && !matchMedia("(max-width:1100px)").matches) open();
      };
      trigger.oncontextmenu = (e) => {
        e.preventDefault();
        open();
      };
      let start: { x: number; y: number } | null = null;
      trigger.onpointerdown = (e) => {
        touch = e.pointerType === "touch";
        if (touch) {
          ignoreClick = false;
          start = { x: e.clientX, y: e.clientY };
          trigger.setPointerCapture(e.pointerId);
        }
      };
      trigger.onpointerup = (e) => {
        if (
          start &&
          start.y - e.clientY > 24 &&
          Math.abs(start.x - e.clientX) < start.y - e.clientY
        ) {
          open();
          ignoreClick = true;
        }
        start = null;
      };
      for (const b of choices) b.onclick = () => choose(b);
      group.onkeydown = (e) => {
        if (["ArrowUp", "ArrowDown", "Home", "End"].includes(e.key)) {
          e.preventDefault();
          if (menu.hidden) {
            this.close();
            menu.hidden = false;
          }
          let i = choices.indexOf(document.activeElement as HTMLButtonElement);
          i =
            e.key === "Home"
              ? 0
              : e.key === "End"
                ? choices.length - 1
                : (i + (e.key === "ArrowDown" ? 1 : -1) + choices.length) %
                  choices.length;
          choices[i]!.focus();
        }
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          this.close();
          trigger.focus();
        }
      };
    }
    document.addEventListener(
      "pointerdown",
      (e) => {
        if (!(e.target as HTMLElement).closest("[data-tool-group-container]"))
          this.close();
      },
      true,
    );
  }
  close() {
    let closed = false;
    for (const menu of document.querySelectorAll<HTMLElement>(
      "[data-tool-group-container] .dropdown-content",
    )) {
      closed ||= !menu.hidden;
      menu.hidden = true;
      menu
        .parentElement!.querySelector("[data-tool-group-trigger]")!
        .setAttribute("aria-expanded", "false");
    }
    return closed;
  }
  render(tool: T | null) {
    for (const [group, button] of this.current)
      group
        .querySelector("[data-tool-group-trigger]")!
        .setAttribute("aria-pressed", String(this.mapping[button.id] === tool));
  }
}
